import { ListActionButton, ModalContext } from '@europrocurement/flexy-components';
import { deleteIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { KeycloakHasRole } from '@europrocurement/l2d-keycloak';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { ExceptionFacturante } from '@europrocurement/l2d-domain/reducers/achats/slices/exceptionFacturante';
import { ExceptionFacturanteApiDeleteExceptionFacturanteItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useAchatsService } from '@europrocurement/l2d-domain';
import { AppDispatch } from '../../../../redux/types';
import { removeExceptionFacturanteModalMessages } from '../../constants/wording/modals';
import ConfirmationModalContent from '../modals/ConfirmationModalContent';

type DeleteExceptionFacturanteButtonProps = {
    exceptionFacturanteId: NonNullable<ExceptionFacturante['id']>;
};

const DeleteExceptionFacturanteButton: React.FunctionComponent<DeleteExceptionFacturanteButtonProps> =
    function ({ exceptionFacturanteId }: DeleteExceptionFacturanteButtonProps) {
        const dispatch = useDispatch<AppDispatch>();
        const { exceptionFacturanteApi, getExceptionFacturante } = useAchatsService();
        const { enqueueSnackbar } = useSnackbar();
        const { modalActions } = useContext(ModalContext);
        const { request } = useApiRequest();

        const onDeleteActionValidated = useCallback(
            (id: NonNullable<ExceptionFacturante['id']>) => {
                if (!id) {
                    enqueueSnackbar(
                        <Typography>Il n&apos;y a aucun mail de rappel sélectionné !</Typography>,
                        {
                            variant: 'error',
                        },
                    );
                    return;
                }

                const successCallback = () => {
                    dispatch(getExceptionFacturante({}));
                };

                const requestParameters: ExceptionFacturanteApiDeleteExceptionFacturanteItemRequest =
                    {
                        id: id.toString(),
                    };

                request(exceptionFacturanteApi.deleteExceptionFacturanteItem(requestParameters), {
                    withToaster: true,
                    successCallback,
                });
            },
            [dispatch, enqueueSnackbar, exceptionFacturanteApi, getExceptionFacturante, request],
        );

        const onDelete = React.useCallback(
            (id: NonNullable<ExceptionFacturante['id']>) => {
                modalActions.call(
                    <ConfirmationModalContent
                        messages={removeExceptionFacturanteModalMessages}
                        actionOnValidation={() => onDeleteActionValidated(id)}
                    />,
                );
            },
            [modalActions, onDeleteActionValidated],
        );

        return (
            <KeycloakHasRole kcRole="realm:interne">
                <ListActionButton
                    tooltip="Supprimer l'exception facturante"
                    onClick={() => {
                        onDelete(exceptionFacturanteId);
                    }}
                    iconComponent={<FaOptionIcon {...deleteIcon.props} />}
                />
            </KeycloakHasRole>
        );
    };

export default DeleteExceptionFacturanteButton;
